import { useMemo } from 'react';
import { Alert, Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { anywherePath, useAnywhereScope } from '../../../service/anywhere';
import AnywhereAdminKnownClientEntry from './AnywhereAdminKnownClientEntry';

const clientIdOf = (client) => client?.idClient ?? client?.id ?? client?.value;
const clientNameOf = (client) => client?.clientName ?? client?.name ?? `Cliente ${clientIdOf(client)}`;
const ADMIN_RESOURCES = [
  ['Terminais', '/terminais'],
  ['Programações', '/programacoes'],
  ['Mídias', '/midias'],
  ['Banco de imagens', '/banco-imagens'],
  ['Conteúdo', '/conteudos'],
  ['Monitoramento', '/monitoramento'],
];

/**
 * Context bar used by every page rendered below an administrative scope.
 * `clients` is intentionally injected by the administrative scope directory query. A known-id
 * fallback remains available when that directory is temporarily unavailable.
 */
export default function AnywhereAdminClientSelector() {
  const { idClient, clients } = useAnywhereScope();
  const location = useLocation();
  const navigate = useNavigate();
  const options = useMemo(
    () => clients.filter((client) => Number.isInteger(Number(clientIdOf(client))) && Number(clientIdOf(client)) > 0),
    [clients]
  );
  const selectedId = Number(idClient);

  const resourcePath = useMemo(() => {
    const marker = `/anywhere/admin/clientes/${selectedId}`;
    const suffix = location.pathname.startsWith(marker) ? location.pathname.slice(marker.length) : '';
    return suffix || '/terminais';
  }, [location.pathname, selectedId]);

  const handleChange = (event) => {
    const nextId = Number(event.target.value);
    if (!Number.isInteger(nextId) || nextId <= 0 || nextId === selectedId) return;
    navigate(anywherePath(`/anywhere/admin/clientes/${nextId}`, resourcePath));
  };

  const handleKnownClient = (nextId) => {
    if (nextId === selectedId) return;
    navigate(anywherePath(`/anywhere/admin/clientes/${nextId}`, resourcePath));
  };

  if (!Number.isInteger(selectedId) || selectedId <= 0) {
    return <Alert severity="error">Cliente administrativo inválido.</Alert>;
  }

  return (
    <Box
      sx={{
        mb: 2,
        px: 2,
        py: 1.5,
        border: 1,
        borderColor: 'primary.main',
        borderRadius: 1,
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-end' }}>
        <BusinessIcon color="primary" />
        {options.length ? (
          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 300 } }}>
            <InputLabel id="anywhere-admin-client-label">Operando o cliente</InputLabel>
            <Select
              labelId="anywhere-admin-client-label"
              value={String(selectedId)}
              label="Operando o cliente"
              onChange={handleChange}
            >
              {options.map((client) => {
                const optionId = Number(clientIdOf(client));
                return (
                  <MenuItem key={optionId} value={String(optionId)}>
                    {clientNameOf(client)} · id {optionId}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        ) : (
          <AnywhereAdminKnownClientEntry onConfirm={handleKnownClient} />
        )}
        <Typography variant="caption" color="text.secondary">
          {options.length
            ? 'Ações ficam auditadas no cliente selecionado.'
            : 'A lista de clientes está indisponível; informe um ID conhecido para continuar.'}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
        {ADMIN_RESOURCES.map(([label, path]) => (
          <Button
            key={path}
            component={RouterLink}
            to={anywherePath(`/anywhere/admin/clientes/${selectedId}`, path)}
            size="small"
            variant={resourcePath === path ? 'contained' : 'text'}
          >
            {label}
          </Button>
        ))}
      </Stack>
    </Box>
  );
}
